import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Route, Routes, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import FAQ from './components/FAQ';
import Current from './components/Current';
import Reference from './components/Reference';
import Discuss from './components/Discuss';
import Home from './components/Home';

const AppContent = () => {
  const { t, i18n } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  const activeSection = location.pathname.slice(1) || 'home';

  const changeLanguage = useCallback((lng) => {
    i18n.changeLanguage(lng);
    Cookies.set('language', lng, { expires: 365 });
  }, [i18n]);

  const initializeLanguage = useCallback(() => {
    const savedLanguage = Cookies.get('language');
    if (savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n]);

  useEffect(() => {
    initializeLanguage();
  }, [initializeLanguage]);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const navItems = [
    { name: 'home', icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    )},
    { name: 'faq', icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )},
    { name: 'current', icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )},
    { name: 'reference', icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
    )},
    { name: 'discuss', icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
      </svg>
    )}
  ];

  return (
    <div className="App min-h-screen bg-gradient-to-br from-purple-500 via-pink-500 to-red-500 flex flex-col">
      <nav className="bg-white bg-opacity-20 p-4 relative z-50">
        <div className="flex justify-between items-center">
          <Link to="/" className="text-white text-xl font-bold">Crystal Path</Link>
          <div className="md:hidden">
            <button onClick={toggleMenu} className="text-white text-2xl p-2">
              {menuOpen ? '✕' : '☰'}
            </button>
          </div>
          <ul className={`md:flex md:space-x-4 ${menuOpen ? 'block' : 'hidden'} absolute md:relative top-full left-0 md:left-auto w-full md:w-auto bg-white bg-opacity-20 backdrop-filter backdrop-blur-lg md:bg-transparent p-4 md:p-0 z-40 rounded-b-xl md:rounded-none`}>
            {['home', 'faq', 'current', 'reference', 'discuss'].map((section) => (
              <li key={section} className="mb-2 md:mb-0">
                <Link
                  to={section === 'home' ? '/' : `/${section}`}
                  className={`text-white px-4 py-2 rounded-lg transition-colors duration-300 block ${activeSection === section ? 'bg-white bg-opacity-30' : 'hover:bg-white hover:bg-opacity-10'}`}
                  onClick={() => {
                    setMenuOpen(false);
                  }}
                >
                  {t(`menu.${section}`)}
                </Link>
              </li>
            ))}
            <li className="md:hidden mt-4">
              <div className="flex justify-center space-x-2">
                <button onClick={() => changeLanguage('en')} className="bg-white text-blue-500 px-2 py-1 rounded">EN</button>
                <button onClick={() => changeLanguage('ru')} className="bg-white text-blue-500 px-2 py-1 rounded">RU</button>
              </div>
            </li>
          </ul>
          <div className="hidden md:flex">
            <button onClick={() => changeLanguage('en')} className="bg-white text-blue-500 px-2 py-1 rounded mr-2">EN</button>
            <button onClick={() => changeLanguage('ru')} className="bg-white text-blue-500 px-2 py-1 rounded">RU</button>
          </div>
        </div>
      </nav>
      <div className="flex-grow flex flex-col items-center p-4 md:p-8 pb-20 md:pb-8">
        <div className="bg-white bg-opacity-20 backdrop-filter backdrop-blur-lg rounded-xl shadow-lg p-4 md:p-8 w-full max-w-4xl">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/current" element={<Current />} />
            <Route path="/reference" element={<Reference />} />
            <Route path="/discuss" element={<Discuss />} />
          </Routes>
        </div>
      </div>
      
      {/* Footer */}
      <footer className="text-white text-center p-4 text-sm md:relative md:bottom-0 mb-16 md:mb-0">
        <p>
          © {new Date().getFullYear()} by{' '}
          <a href="https://wastelandw.ru" className="underline hover:text-blue-300" target="_blank" rel="noopener noreferrer">wasteland_w</a>
          {' '}and{' '}
          <a href="https://neiro.network" className="underline hover:text-blue-300" target="_blank" rel="noopener noreferrer">neiro.network</a>
        </p>
      </footer>
      
      {/* Mobile bottom navigation */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white bg-opacity-20 backdrop-filter backdrop-blur-lg">
        <ul className="flex justify-between py-1">
          {navItems.map(({ name, icon }) => (
            <li key={name} className="flex-grow">
              <Link
                to={name === 'home' ? '/' : `/${name}`}
                className={`text-white p-1 rounded-lg transition-colors duration-300 flex flex-col items-center justify-center h-full ${
                  activeSection === name ? 'bg-white bg-opacity-30' : 'hover:bg-white hover:bg-opacity-10'
                }`}
              >
                <div className="h-5 w-5">{icon}</div>
                <span className="text-[10px] mt-0.5">{t(`menu.${name}`)}</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const App = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default App;